import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import css from './roadMap.module.css';
import { getLearningPathById, getLearningPaths } from '../../lib/learningPath';
import { trackStarterPlanShown } from '../../lib/analytics';

const RoadMap = ({ authUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // State for user data
  const [userLevel, setUserLevel] = useState(null);
  const [userPurpose, setUserPurpose] = useState(null);
  const [progress, setProgress] = useState(0);
  const [learningPath, setLearningPath] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pathTopics, setPathTopics] = useState([]);

  // Track starter plan shown when component mounts
  useEffect(() => {
    trackStarterPlanShown();
  }, []);

  // Load user data and learning path from localStorage and API
  useEffect(() => {
    const fetchLearningPath = async () => {
      setIsLoading(true);
      try {
        const selectedLevel = localStorage.getItem("selectedLevel");
        const selectedPurpose = localStorage.getItem("selectedPurpose");
        const learningPathId = localStorage.getItem("currentLearningPathId");
        
        console.log('RoadMap - Selected Level:', selectedLevel);
        console.log('RoadMap - Selected Purpose:', selectedPurpose);
        console.log('RoadMap - Learning Path ID:', learningPathId);
        
        // Set user level
        if (selectedLevel) {
          const parsedLevel = parseInt(selectedLevel);
          if (!isNaN(parsedLevel)) {
            setUserLevel(parsedLevel);
          } else {
            setUserLevel(2);
            localStorage.setItem("selectedLevel", "2");
          }
        } else {
          setUserLevel(2);
          localStorage.setItem("selectedLevel", "2");
        }
        
        // Set user purpose
        let purposeData = null;
        if (selectedPurpose) {
          try {
            purposeData = JSON.parse(selectedPurpose);
            if (purposeData && typeof purposeData === 'object') {
              setUserPurpose(purposeData);
            }
          } catch (error) {
            console.error("Error parsing purpose data:", error);
            localStorage.removeItem("selectedPurpose");
          }
        }
        
        // Build filters for API
        const filters = {};
        if (selectedLevel) {
          filters.level = parseInt(selectedLevel);
        }
        if (purposeData && purposeData.id) {
          filters.purpose = purposeData.id;
        }

        console.log('RoadMap - API Filters:', filters);
        
        // Fetch learning path
        let pathData = null;
        
        if (learningPathId) {
          // If we have a specific path ID, fetch that path
          console.log('Fetching specific learning path:', learningPathId);
          pathData = await getLearningPathById(learningPathId, filters);
          console.log('Fetched learning path by ID:', pathData);
        } else {
          // Otherwise, fetch all paths and use the first one
          console.log('Fetching all learning paths with filters:', filters);
          const pathsResponse = await getLearningPaths(filters);
          console.log('Fetched learning paths:', pathsResponse);
          
          if (pathsResponse.results && pathsResponse.results.length > 0) {
            pathData = pathsResponse.results[0];
            // Store the ID for future use
            localStorage.setItem('currentLearningPathId', pathData.id);
            console.log('Using first learning path:', pathData);
          }
        }

        if (pathData) {
          setLearningPath(pathData);
          
          // Parse topics
          if (pathData.topics) {
            try {
              const topics = typeof pathData.topics === 'string' 
                ? JSON.parse(pathData.topics) 
                : pathData.topics;
              console.log('Parsed topics:', topics);
              setPathTopics(Array.isArray(topics) ? topics : []);
            } catch (e) {
              console.error("Error parsing topics:", e);
              setPathTopics([]);
            }
          }
          
          // TODO: Calculate progress from user data
          setProgress(0);
        } else {
          console.log('No learning path found');
        }
      } catch (error) {
        console.error("Error fetching learning path:", error);
        console.error("Error details:", error.response?.data);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLearningPath();
  }, []);

  // Get level name based on level number
  const getLevelName = (level) => {
    const levelNames = {
      1: t("Beginner"),
      2: t("Elementary"), 
      3: t("Intermediate"),
      4: t("Upper-Intermediate"),
      5: t("Advanced"),
      6: t("Proficiency")
    };
    return levelNames[level] || t("Beginner");
  };

  // Handle navigation buttons
  const handleLowerLevel = () => {
    if (userLevel && userLevel > 1) {
      const newLevel = userLevel - 1;
      setUserLevel(newLevel);
      localStorage.setItem("selectedLevel", newLevel.toString());
    }
  };

  const handleRaiseLevel = () => {
    if (userLevel && userLevel < 6) {
      const newLevel = userLevel + 1;
      setUserLevel(newLevel);
      localStorage.setItem("selectedLevel", newLevel.toString());
    }
  };

  const handleAddPlan = () => {
    // Navigate to survey without language selection
    navigate('/survey?skipLanguage=true');
  };

  const handleChooseVideo = () => {
    localStorage.removeItem("selectedPurpose")
    localStorage.removeItem("selectedLevel");
    navigate('/catalog');
  };

  const handleContinueLearning = () => {
    // Navigate to first recommended video or lesson
    // TODO: Implement logic to get first recommended content
    navigate('/catalog');
  };

  // Get categories from learning path or use default
  const getCategories = () => {
    // If we have topics from the learning path, use them
    if (pathTopics && pathTopics.length > 0) {
      return pathTopics.map((topic, index) => ({
        id: index + 1,
        icon: getTopicIcon(topic.name),
        title: topic.name,
        topics: topic.lessons ? topic.lessons.map(lesson => lesson.name || lesson.title) : [],
        active: index === 0 // Highlight first topic by default
      }));
    }

    // Fallback to default categories
    const baseCategories = [
      {
        id: 1,
        icon: '👋',
        title: t("Basics"),
        topics: [t("Introduction"), t("Greetings"), t("Practice")],
      },
      {
        id: 2,
        icon: '💬',
        title: t("Conversations"),
        topics: [t("Daily Talk"), t("Questions"), t("Social")],
      },
      {
        id: 3,
        icon: '📁',
        title: t("Work"),
        topics: [t("Business"), t("Emails"), t("Meetings")],
      },
      {
        id: 4,
        icon: '✈️',
        title: t("Travel"),
        topics: [t("Airport"), t("Hotels"), t("Dining")],
      },
      {
        id: 5,
        icon: '🎯',
        title: t("Advanced"),
        topics: [t("Grammar"), t("Idioms"), t("Fluency")],
      },
      {
        id: 6,
        icon: '🎯',
        title: t("Advanced"),
        topics: [t("Grammar"), t("Idioms"), t("Fluency")],
      }
    ];

    // Highlight category based on user purpose
    if (userPurpose && userPurpose.purpose) {
      const purposeText = userPurpose.purpose.toLowerCase();
      baseCategories.forEach(category => {
        category.active = false;
        
        if (purposeText.includes('travel') && category.title.toLowerCase().includes('travel')) {
          category.active = true;
        } else if (purposeText.includes('job') || purposeText.includes('work')) {
          if (category.title.toLowerCase().includes('work')) category.active = true;
        } else if (purposeText.includes('communication')) {
          if (category.title.toLowerCase().includes('conversations')) category.active = true;
        } else if (userLevel && category.id === userLevel) {
          category.active = true;
        }
      });
    }

    return baseCategories;
  };

  // Helper function to get icon for topic
  const getTopicIcon = (topicName) => {
    const name = topicName?.toLowerCase() || '';
    if (name.includes('basic') || name.includes('introduction')) return '👋';
    if (name.includes('conversation') || name.includes('talk')) return '💬';
    if (name.includes('work') || name.includes('business')) return '📁';
    if (name.includes('travel')) return '✈️';
    if (name.includes('advanced') || name.includes('grammar')) return '🎯';
    return '📚';
  };

  if (isLoading) {
    return (
      <div className={css.container}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{t("Loading...")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={css.container}>
      <h1 className={css.title}>
        {learningPath ? learningPath.name : t("English Learning Journey")}
      </h1>
      
      <div className={css.navButtons}>
        <button 
          className={css.navBtn}
          onClick={handleLowerLevel}
          disabled={!userLevel || userLevel <= 1}
        >
          ← {t("Lower Level")}
        </button>
        <button 
          className={css.navBtn}
          onClick={handleAddPlan}
        >
          + {t("Add Plan")}
        </button>
        <button 
          className={css.navBtn}
          onClick={handleRaiseLevel}
          disabled={!userLevel || userLevel >= 6}
        >
          {t("Raise Level")} →
        </button>
      </div>
      <div className={css.levelName}>{getLevelName(userLevel)}</div>
      <div className={css.progressSection}>
        <div className={css.progressNumber}>{progress}%</div>
        <div className={css.progressLabel}>{t("Learning Progress")}</div>
      </div>
      
      <div className={css.categories}>
        {getCategories().map((category) => (
          <div 
            key={category.id} 
            className={`${css.category}`}
          >
            <div className={css.categoryIcon}>
              {category.icon}
              <div className={css.categoryNumber}>{category.id}</div>
            </div>
            <div className={css.categoryTitle}>{category.title}</div>
            <div className={css.topics}>
              {category.topics.map((topic, index) => (
                <div key={index} className={css.topic}>
                  {topic}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className={css.bottomButtons}>
        <button 
          className={css.chooseVideoBtn}
          onClick={handleChooseVideo}
        >
          🎬 {t("I want to choose the video myself")}
        </button>
        <button 
          className={css.continueBtn}
          onClick={handleContinueLearning}
        >
          {t("Continue Learning")} →
        </button>
      </div>
    </div>
  );
};

export default RoadMap;